"use client";

import GetInstantQuoteButton from "@/components/Contact/GetInstantQuote";
import DetailPageBanner from "./DetailPageBanner";
import MyBreadcrumb from "./MyBreadcrumb";
import Image from "next/image";
import { useTranslations } from "next-intl";

export interface MaterialDetailProps {
	slug: string;
	breadcrumbTitle2: string;
	materialName: string;
	description: string;
	advantages: string;
	disadvantages: string;
	applications: string;
	tolerance: string;
	wallThickness: string;
	deliveryTime: string;
	maxPartSize: string;
	surfaceTreatment: string;
	imagePath?: string;
	prefix: string;
}

const MaterialDetailPage = ({
	slug,
	breadcrumbTitle2,
	materialName,
	description,
	advantages,
	disadvantages,
	applications,
	tolerance,
	wallThickness,
	deliveryTime,
	maxPartSize,
	surfaceTreatment,
	imagePath,
	prefix,
}: MaterialDetailProps) => {
	const t = useTranslations();

	// 使用 imagePath 如果提供，否则使用默认路径
	const imageSrc = imagePath || `/image/material/${slug}.jpg`;

	// Description and titles
	const descriptionText = `${t("materials.CNC machining Materials")} - ${materialName}`;
	const bannerTitle = t("materials.CNC machining materials");
	const descriptionTitle = descriptionText;

	// Get translated material name from slug for breadcrumb
	const getMaterialNameFromSlug = (slug: string, prefix: string): string => {
		// Try metals first
		const detail = t(`${prefix}-detail-${slug}.materialName`, { defaultValue: "" });
		if (detail) return detail;

		return "";
	};

	const translatedMaterialName = getMaterialNameFromSlug(slug, prefix);

	return (
		<div>
			<MyBreadcrumb
				title1={t("materials.Materials")}
				title2={breadcrumbTitle2}
				title3={translatedMaterialName}
			/>
			<DetailPageBanner
				title={bannerTitle}
				materialName={materialName}
				description={description}
				buttonText={t("materials.Customized CNC parts", { material: materialName })}
				buttonHref="/upload"
			/>
			<div>
				<div className="flex">
					<div className="flex w-1/2 flex-col items-center justify-center bg-black text-white">
						<h3 className="mb-4 text-3xl font-bold">{descriptionTitle}</h3>
						<p className="max-w-[70%] text-base">{descriptionText}</p>
					</div>
					<div className="aspect-square w-1/2 bg-[#f2f2f4]">
						<Image
							src={imageSrc}
							alt={slug}
							width={1000}
							height={1000}
							className="h-full w-full object-contain"
						/>
					</div>
				</div>
				<div className="container flex">
					<div className="w-1/2 px-[56px] py-[48px]">
						<h3 className="mb-4 flex items-center gap-2 text-2xl font-bold">
							<Image
								width={66}
								height={55}
								src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeAAAACXBIWXMAAAsTAAALEwEAmpwYAAAB7UlEQVR4nO2ZQUrDQBSGP/UAKmi9gOha8BaCe8F7KGj1EAqexE1X2hq0HkVUrLpQBJ8MzCKLpBkzk+kkfT8MlHnT15cvkzc/DahUKpVKpVKpVHWUAdLwGJGwJNJIVtJwgQoABYACIGGJAqCwwBjHYxJHqZQAmPUFRztKpQJAiFwx8tbWXAHIGt52yQMQBYBzIgWAAqBuobN4tJIAMHK8+GFXATQlBYACQAEQOZG0HUBW0/n5HFdNqVYtEhnALnANPAX8ba//CcTzDv7n+3vAT+QLr7xJEgnAGvBu114A63Zc2bkJsOFRR+0aJRKAM7vuFljIzZvPdzZ27lFH8gDu7bqDgtihjd3QYQCfdt1mQWzLxl7pKIAl4NeuWy6Ir9jYt0cdye+AyZQdsG1jz3QYwKNDDzANsrMAThxOgSOPOpxqzGKbDGAHGOQ8gBmX1gP0cj4gihGSyAD2ga+IF+gMIKTKchpX9zLF+eVHmTPsha5RIgI4dnjmq+L9NgN4cOj6VfFx6BqloNixtal15spyGr05OL+quHGPjQMQj7my+UVH5+cSNy6ydQDIHXs+O+CDMJoKYFTwAsJ1riwnuUbm0wNMH2ltE+wHOAVO2wxgNdcIq5xfUbxRHyBzOnB9KdnFMQy0m1QqlUqlUtE+/QG+ulr0L1K2OgAAAABJRU5ErkJggg=="
								alt="construction-materials"
							/>
							{t("materials.Material Description")}
						</h3>
						<ul className="flex flex-col gap-3">
							<li className="flex items-start gap-4">
								<span className="w-32 flex-shrink-0 font-semibold">{t("materials.Advantages")}</span>
								<span className="text-gray-700">{advantages}</span>
							</li>
							<li className="flex items-start gap-4">
								<span className="w-32 flex-shrink-0 font-semibold">{t("materials.Disadvantages")}</span>
								<span className="text-gray-700">{disadvantages}</span>
							</li>
							<li className="flex items-start gap-4">
								<span className="w-32 flex-shrink-0 font-semibold">{t("materials.Applications")}</span>
								<span className="text-gray-700">{applications}</span>
							</li>
						</ul>
					</div>
					<div className="w-1/2 border-l px-[56px] py-[48px]">
						<h3 className="mb-4 flex items-center gap-2 text-2xl font-bold">
							<Image
								width={66}
								height={66}
								src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEIAAABCCAYAAADjVADoAAAACXBIWXMAAAsTAAALEwEAmpwYAAAIbklEQVR4nO1bB4xVRRQ9u4riYlnFsmBAY8Regh1jb6gIip1oVLBiSzB2DRJLFHtBxYZdIzawYm+oYAmKQWyoCAgiNkRcYPGba84kJ5OZ9+a//ftZw57kBXgz9878eTP3nnvnArShDW1oxegC4FUAowEsh6UUWwGYAaDEZwMshegF4E9ZhKFYCnECgCZZBFuQRwBcD2AQgD0ArISlAD/JIsQeW6iJAK4A0B3/Q2wI4G4AnwPYO9LnCu9HjwfwGYDfMhbmCwADAayAVo7uAJ4EsFgmf1ukby2Ax6XfPdK2GhfwEgBvekfIntkAzm2NHmZFADd4E7bFeAxA5ww5+7LviczpkX4NAM7gjtAF+RLAfmgl2AfAVJlcI4A7AKyfKL8GgG8oOyKnr+2iPgAmeAsyfEkfl7O9XfBGQR5gu+ZUAKsn9l8GwMkAfpGxJ5ax+BXD8gDulUnMBTAAQE2V57E2Gambh3miras1+LKkwm7wKQA2K6inE4AtAWzBH2ULXC7suFzmfZRd0MKoAXC/DPp2GdvZoQ7A+xku0s5/hwJz6w9gIXX8zsVtMVwrE36/4IT7JBCpgwrOr5/YrOkM5iqOPgD+4SCf0dcXwQXUYbqOBrA/n36i/+JmzPMkWdB3eQQrhs4AfqbyX3JWupY/xOzINoG2p6jn+4Dst2wbxb6KbQG8QEaa9+NukcW4HBXEGFHcN6OfBUnPSt8FZIAdyRJ/kLbnAvLPSPs0AENog86V82/PKwBWzZhHO7FDTQz1m42eMgEjSjGsxSMTOvOLvH/bAh0a0HEI27JklVWap4lhY5I76zu2ua69VljcrxlfwTzBBzJJC6d7AJgcCKwGkjJnLehAT1+JurZlMOfefZITqg+WvmaDCqOfKLLtGVss5RW3yup3oKexo9W7wPgm8yKA6zwPdY2MNybDZpjMTDHwhXfFO1QyI4PL7+hNyjdyLYEa2hg37k4Zfc+UfoUCtA3EnV2a0a9ekixzyBZT0EC3eRzJ0P45R8b3YnOEVtscsiLj3zMMdC6uEn+/Xk7fA2XVX8zYgvb+SFp0zVlo6G5tR+ToeEFkbGwkutOFRfjPlxR+PbH/fTK5nQPt6wAYl8Aqlbl2DejZWfoY3U/BDiJzPMpAQ4KR9DFS/Lb9aD9zpTnK6eQIPbjNO9PWDGGb6zcrkKvsKi7VMlwpqKWucmT+w+EyGVvNPKwrHP8Br60TyZHTd3VOEqXO8wrTAnbHBX5NHDsF94vhT8bNFJpHlpaH82Tim3tto6TttDLmcJrIPe21bS5tFruk4HSR8XdsFC9TwM50LL02mHHD12L4zK7EXGteGi6EESJvuhQuf7mYqb6nSeNjnkfnYmw5Cd9Q4OFIu+Yk9PGjxrv4/q8yXKOigbIl6lJcGJnDYxm6XB+LUJMwT85zCC6g+ZnG5xJe3/lkyhmoJ1AcT1CHMURFLcccTEM9m/0+juipFSNrGa2kxOg/kS/sMEXodAz18gWM3RWFMsP6BLtmGfUYnOeyq4dc1MnAlqUOYS7br8zQs5HoMS9UFIeKHtOJnJsz280xuB1qi5a0I0p87ByG4JI0FlDF0EX0GI0uimNFT1ZC6GpJHOUtxLDUwRspYKscwmTh7t0igVY74RZZOycPVwpnCLnyWt5nuISQeZMYNW8sN2v1XQ6Ffduz1H8xUvXT+mPZPgnFMYk6TL9iU87DGXb32JghrFaEz7xFAfszhP6BTFKJvEJxVmKKL4a+Im81E4onA+Mv4EVTCN2k38GpExhOgd8yosAVeKs0QNzpfC+BspJY6hBVzkKDUPNZXiaqg/CLcZzDNjnUfRnyopcArFxONUuJT8pdZi/p73+R46Tt00hE6aMr03BOzgymvyNdW5HMVzI2LfM8tZMM9deB1Nkw0TeH3MDctI86trmkS4m5BP/LfiW7LCUWahamcjBLgqTgUpn87gHLPtQ7z/OZ2ruDR3EM32mfqwIeaTdpT2KIzcUwDraImWWQo98ZyGbXy46YmxFX9BIvkPVMYuouhAYhdNNy7jcqgu1kYufwkqYpErQ8VEYGyLb2vtwF45mImcG/D2eb9cnCABkvFhhWFBNl5Y+QwW17Omwt71OPUSWgWWz7aIqVWZ54IfMhZlMuas5gx8hgbuv/DaC99FlXzvZsVtT5uYvmXLmZ7Jreu/WkWncBOQIY00yKJIYby62/2Ix09SYqdnGFe6zKLRQYLZYF24TvjwLwB99b0dheiZcs1mdP3mY7u+NcaDeJft2NusOHObbH3zmZuClHmSVYQxgkfX6VW2//mUoPEjJyq9JTfB+RHeV9GD8otMzTo5xj/wD9tsq8sjjEqzwCocn4rlFxZqAmciZrLbXmOhYK3+j1MZnbAfzovV/MPGkMtZJudJ7P/nwQBdCetUiWAXqNdmBCQpFnT6mefV7Od0d+EUebzUv4cFR9OvuaDFgaMFqOSe8yksmPi6yfU21xdOJ9ZE3G0fvTa68RfuCzSde+Y04pAHi/uVDsVUd6DGdTihSstQhOlK81mUHTONZtu/dWQ1kEPeVIz5dSwy4cIzkhUw3onUTsKVIRt7e48UUFw/7C2IRXgo6Cp+JA3maNYLnQsyxgvaZgRd0Zkn1q8lxqVTBeiFTRksDmoN5L0DSy/KjqGOJt6XvIJFsaZjgP8/iGVeRtjyWIvh7JmUdStEoLjddDKnnc81Q1otBUd6llhY5d3lKh0j5LvJ4ilLskKbxYjnKJYjfvP6KUpJDrBtoRR5KyUEceMoiXu84QumcuiZ6VBbVqHMAdEquRnMm0+2jmE0Yyqfoeo8eY3Czec1TDDlX8yJzD+wf/q6Y+s+hae1cjP1kNWOyyK4DzecVvQdFHDKmnMA4YS8M3lLmQrLvONrShDWhx/Avwn6VkJjBADgAAAABJRU5ErkJggg=="
								alt="external-processing-business-management-smashingstocks-detailed-outline-smashing-stocks"
							/>
							{t("materials.Processing Characteristics")}
						</h3>
						<ul className="flex flex-col gap-3">
							<li className="flex items-start gap-4">
								<span className="w-36 flex-shrink-0 font-semibold">{t("materials.Tolerance")}</span>
								<span className="text-gray-700">{tolerance}</span>
							</li>
							<li className="flex items-start gap-4">
								<span className="w-36 flex-shrink-0 font-semibold">{t("materials.Wall Thickness")}</span>
								<span className="text-gray-700">{wallThickness}</span>
							</li>
							<li className="flex items-start gap-4">
								<span className="w-36 flex-shrink-0 font-semibold">{t("materials.Delivery Time")}</span>
								<span className="text-gray-700">{deliveryTime}</span>
							</li>
							<li className="flex items-start gap-4">
								<span className="w-36 flex-shrink-0 font-semibold">{t("materials.Maximum Part Size")}</span>
								<span className="text-gray-700">{maxPartSize}</span>
							</li>
							<li className="flex items-start gap-4">
								<span className="w-36 flex-shrink-0 font-semibold">{t("materials.Surface Treatment")}</span>
								<span className="text-gray-700">{surfaceTreatment}</span>
							</li>
						</ul>
					</div>
				</div>

				<div className="flex flex-col items-center justify-center bg-[#191919] py-[128px]">
					<p className="mb-10 text-center text-2xl font-bold text-white">
						{t("materials.Upload your design files and start production immediately")}
					</p>
					<GetInstantQuoteButton className="max-md:hidden" />
				</div>
			</div>
		</div>
	);
};

export default MaterialDetailPage;
